import { ref, Ref } from 'vue';
import api from '@/services/api';

// Interfaces
export interface Picture {
  id: string;
  path: string;
  isMain: boolean;
}

// Ažurirana definicija kategorije koja uključuje parent polje
export interface Category {
  id: string;
  name: string;
  parent?: {
    // Dodajemo parent kao opcionalno polje
    id: string;
    name: string;
  };
}

export interface User {
  id: string;
  username: string;
  city: string;
  positiveRatings: number;
  negativeRatings: number;
  followersCount: number;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  stock: number;
  createdAt: string;
  renewedAt: string;
  category: Category;
  pictures: Picture[];
  user: User;
  followersCount: number;
}

interface ProductResponse {
  status: string;
  message: string;
  data: Product;
}

export function useProductDetail() {
  const product: Ref<Product | null> = ref(null);
  const loading = ref(true);
  const error: Ref<string | null> = ref(null);
  const apiUrl = process.env.VUE_APP_API_URL;

  const fetchProductDetails = async (slug: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get<ProductResponse>(
        `/api/products/detailed/${slug}`
      );

      if (response.data.status === 'success') {
        product.value = response.data.data;
      } else {
        error.value = 'Greška prilikom učitavanja proizvoda.';
      }
    } catch (err: any) {
      console.error('Error fetching product details:', err);

      // Provera za 404 grešku
      if (err?.response?.status === 404) {
        error.value = 'Oglas više nije aktivan ili je istekao.';
      } else {
        error.value = 'Greška prilikom komunikacije sa serverom.';
      }
    } finally {
      loading.value = false;
    }
  };

  const formatPrice = (price: string): string => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: 'RSD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(price));
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('sr-Latn-RS', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  return {
    product,
    loading,
    error,
    apiUrl,
    fetchProductDetails,
    formatPrice,
    formatDate,
  };
}
